import { View } from '@play-co/timestep-core/ui';
import {
  particleStream,
  ParticleStreamNode,
} from 'src/lib/effects/particleStream';
import {
  applyRelativeOffset,
  delay,
  animDuration,
  getFooter,
  getHeader,
  getSceneManager,
} from 'src/lib/utils';
import { toParticleAmount } from './animation.util';
import { user, now } from 'src/StateObserver';
import playExplosion from 'src/lib/effects/particleExplosion';
import {
  Match2BoosterType,
  Match2PowerupType,
} from 'src/replicant/match2/match2.ruleset';
import {
  getActiveRace,
  getRaceStartTime,
} from 'src/replicant/match2/races/races.getters';
import { getRaceTheme } from '../minigame/match2-timestep/races/races.utils';
import { RacesButtonAnimated } from '../minigame/match2-timestep/races/components/RacesButtonAnimated';
import { getMatch2FrenzyEventConfig } from 'src/replicant/match2/frenzy/match2-frenzy.getters';
import { TeamCompetitionButton } from '../minigame/match2-timestep/team/components/TeamCompetitionButton';
import { getFrenzyObjectiveIcon } from '../minigame/match2-timestep/match2.icons';
import { getMatch2AssetPath } from '../minigame/match2-timestep/game/util/assets';
import { matchTheme } from '../theme/match2/Match2Theme';

export async function animateCoins({
  superview,
  count,
  start,
  end,
  duration,
}: {
  superview: View;
  count: number;
  start: ParticleStreamNode;
  end?: ParticleStreamNode;
  duration?: number;
}) {
  if (!count) return;

  if (!getHeader()) {
    console.warn(
      'Could not play coin animation because scene manager is not available.',
    );
    return;
  }

  await particleStream({
    superview,
    start,
    end: end ?? {
      position: getHeader().getCoinIconPos(superview),
      scale: 0.5,
    },
    count: toParticleAmount(count),
    image: 'assets/ui/hud/icons/hud_coin.png',
    sfxBegin: 'progressCounted',
    sfxFinish: 'progressAwarded',
    duration,
  });
}

export async function animateGems({
  superview,
  count,
  start,
  end,
  duration,
}: {
  superview: View;
  count: number;
  start: ParticleStreamNode;
  end?: ParticleStreamNode;
  duration?: number;
}) {
  if (!count) return;

  if (!getHeader()) {
    console.warn(
      'Could not play gem animation because scene manager is not available.',
    );
    return;
  }

  await particleStream({
    superview,
    start,
    end: end ?? {
      position: getHeader().getGemsIconPos(superview),
      scale: 0.5,
    },
    count: toParticleAmount(count),
    image: 'assets/ui/hud/icons/hud_gem.png',
    sfxBegin: 'progressCounted',
    sfxFinish: 'progressAwarded',
    duration,
  });
}

export async function animateEnergy({
  superview,
  count,
  start,
  end,
  duration,
}: {
  superview: View;
  count: number;
  start: ParticleStreamNode;
  end?: ParticleStreamNode;
  duration?: number;
}) {
  if (!count) return;

  if (!getHeader()) {
    console.warn(
      'Could not play energy animation because scene manager is not available.',
    );
    return;
  }

  await particleStream({
    superview,
    start,
    end: end ?? {
      position: getHeader().getEnergyPos(superview),
      scale: 0.5,
    },
    count: toParticleAmount(count),
    image: 'assets/ui/hud/icons/hud_energy.png',
    sfxBegin: 'progressCounted',
    sfxFinish: 'progressAwarded',
    duration,
  });
}

/**
 * Energy explosion animation
 */
export async function animateEnergyExplosion({
  superview,
  count,
  start,
  duration,
}: {
  superview: View;
  count: number;
  start: { x: number; y: number };
  duration?: number;
}) {
  playExplosion({
    superview,
    sc: 2.2,
    image: 'assets/ui/shared/icons/energy.png',
    max: toParticleAmount(count, 18),
    startX: start.x,
    startY: start.y,
    spreadMode: 'burst',
  });

  await delay(duration ?? animDuration);
}

/**
 * Coins explosion animation
 */
export async function animateCoinsExplosion({
  superview,
  count,
  start,
  duration,
}: {
  superview: View;
  count: number;
  start: { x: number; y: number };
  duration?: number;
}) {
  playExplosion({
    superview,
    sc: 2,
    image: 'assets/ui/shared/icons/coin.png',
    max: toParticleAmount(count, 30),
    startX: start.x,
    startY: start.y,
    spreadMode: 'burst',
  });

  await delay(duration ?? animDuration);
}

/**
 * Gems explosion animation
 */
export async function animateGemsExplosion({
  superview,
  count,
  start,
  duration,
}: {
  superview: View;
  count: number;
  start: { x: number; y: number };
  duration?: number;
}) {
  playExplosion({
    superview,
    sc: 2.5,
    image: 'assets/ui/shared/icons/gem.png',
    max: toParticleAmount(count, 20),
    startX: start.x,
    startY: start.y,
    spreadMode: 'burst',
  });

  await delay(duration ?? animDuration);
}

// -- Match2 powerups
export async function animateMatch2Powerups({
  superview,
  type,
  count,
  start,
  end,
  duration,
}: {
  superview: View;
  type: Match2PowerupType;
  count: number;
  start: ParticleStreamNode;
  end?: ParticleStreamNode;
  duration?: number;
}) {
  if (!count) return;

  if (!getFooter()) {
    console.warn(
      'Could not play gem animation because scene manager is not available.',
    );
    return;
  }

  let image = '';
  let assetType = 'icon.bomb';

  switch (type) {
    case 'rocket':
      assetType = 'icon.rocket.horizontal';
      break;
    case 'cube':
      assetType = 'power/block.cube.all';
      break;
  }

  image = getMatch2AssetPath(`${assetType}.png`);

  await particleStream({
    superview,
    start,
    end: end ?? {
      position: getFooter().getMatch2PlayIconPos(superview),
      scale: 0.5,
    },
    count: toParticleAmount(count),
    image,
    sfxBegin: 'progressCounted',
    sfxFinish: 'progressAwarded',
    duration,
  });
}

export async function animateMatch2Boosters({
  superview,
  type,
  count,
  start,
  end,
  duration,
}: {
  superview: View;
  type: Match2BoosterType;
  count: number;
  start: ParticleStreamNode;
  end?: ParticleStreamNode;
  duration?: number;
}) {
  if (!count) return;

  if (!getFooter()) {
    console.warn(
      'Could not play gem animation because scene manager is not available.',
    );
    return;
  }

  await particleStream({
    superview,
    start,
    end: end ?? {
      position: getFooter().getMatch2PlayIconPos(superview),
      scale: 0.5,
    },
    count: toParticleAmount(count),
    image: matchTheme().getBoosterIcon(type),
    sfxBegin: 'progressCounted',
    sfxFinish: 'progressAwarded',
    duration,
  });
}

export async function animateTeamEventPoints({
  superview,
  count,
  start,
  end,
  duration,
  type,
}: {
  superview: View;
  count: number;
  start: ParticleStreamNode;
  end?: ParticleStreamNode;
  duration?: number;
  type: 'team_competition_event' | 'team_chest_event' | 'team_event';
}) {
  if (!count) return;

  const icon = getSceneManager()?.currentScene?.getIcon('teamEvent');
  if (!icon) {
    console.warn('Could not play animation because icon is not available.');
    return;
  }

  const parent = icon.getSuperview()!;
  const position = icon.getPosition(parent);
  applyRelativeOffset(position, parent, superview);

  const iconClone = new TeamCompetitionButton({
    superview,
    tracking: {
      feature: 'mock',
      $subFeature: 'mock-icon',
    },
    onClick: async () => void 0,
    x: position.x,
    y: position.y,
    zIndex: 9000,
  });

  // icon
  position.x += icon.style.width * 0.5;
  position.y += icon.style.height * 0.5;

  await particleStream({
    superview,
    start,
    end: end ?? {
      position,
      scale: 1,
    },
    count: toParticleAmount(count),
    image:
      type === 'team_chest_event'
        ? 'assets/ui/match2/team/chest/team_chest_icon.png'
        : 'assets/ui/match2/team/competition/icon_teamCompetition_sm.png',
    sfxBegin: 'progressCounted',
    sfxFinish: 'progressAwarded',
    duration,
  });
  iconClone.removeFromSuperview();
}

export async function animateMatch2RacePoints({
  superview,
  raceId,
  count,
  start,
  end,
  duration,
}: {
  superview: View;
  raceId: string;
  count: number;
  start: ParticleStreamNode;
  end?: ParticleStreamNode;
  duration?: number;
}) {
  if (!count) return;

  const icon = getSceneManager()?.currentScene?.getIcon('race');
  if (!icon) {
    console.warn('Could not play animation because icon is not available.');
    return;
  }

  const { config } = getActiveRace(getRaceStartTime(raceId))!;

  if (config.type === 'target') return;

  const parent = icon.getSuperview()!;
  const position = icon.getPosition(parent);
  applyRelativeOffset(position, parent, superview);

  const iconClone = new RacesButtonAnimated({
    superview,
    tracking: {
      feature: 'mock',
      $subFeature: 'mock-icon',
    },
    onClick: async () => void 0,
    x: position.x,
    y: position.y,
    zIndex: 9000,
  });

  // icon
  position.x += icon.style.width * 0.5;
  position.y += icon.style.height * 0.5;

  await particleStream({
    superview,
    start,
    end: end ?? {
      position,
      scale: 1,
    },
    count: toParticleAmount(count),
    image: getRaceTheme(config).icon.image as string,
    sfxBegin: 'progressCounted',
    sfxFinish: 'progressAwarded',
    duration,
  });
  iconClone.removeFromSuperview();
}

export async function animateMatch2FrenzyObjective({
  superview,
  count,
  start,
  end,
  duration,
}: {
  superview: View;
  count: number;
  start: ParticleStreamNode;
  end?: ParticleStreamNode;
  duration?: number;
}) {
  if (!count) return;

  if (!getHeader().isMatch2FrenzyBarVisible()) {
    console.warn(
      'Could not play gem animation because frenzy bar is not visible.',
    );
    return;
  }

  const config = getMatch2FrenzyEventConfig(user(), now());

  if (!config) {
    console.warn(
      `Could not play gem animation because no frenzy event config was found.`,
    );
    return;
  }

  const objectivePosition =
    getHeader().getMatch2FrenzyBarObjectivePos(superview);
  if (!objectivePosition) return;

  const image = getFrenzyObjectiveIcon(config.theme).getCached();

  await particleStream({
    superview,
    start,
    end: end ?? {
      position: objectivePosition,
      scale: 1,
    },
    count: toParticleAmount(count),
    image: { image },
    sfxBegin: 'progressCounted',
    sfxFinish: 'progressAwarded',
    duration,
  });
}

export async function animateMatch2Moves({
  superview,
  count,
  start,
  end,
  duration,
}: {
  superview: View;
  count: number;
  start: ParticleStreamNode;
  end?: ParticleStreamNode;
  duration?: number;
}) {
  if (!count) return;

  if (!getFooter()) {
    console.warn(
      'Could not play gem animation because scene manager is not available.',
    );
    return;
  }

  await particleStream({
    superview,
    start,
    end: end ?? {
      position: getFooter().getMatch2PlayIconPos(superview),
      scale: 0.5,
    },
    count: toParticleAmount(count),
    image: `assets/ui/match2/common/moves.png`,
    sfxBegin: 'progressCounted',
    sfxFinish: 'progressAwarded',
    duration,
  });
}
